// ============================================
// OPTIONS TRADE SCANNER - SCANNER PAGE v3.1
// Protected Scanner with Usage Tracking
// ============================================

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserContext';
import { TickerAnalysis } from '../types';
import { runFullAnalysis } from '../services/apiService';
import { TickerInput } from '../components/TickerInput';
import { AnalysisProgress } from '../components/AnalysisProgress';
import { ScoreCard } from '../components/ScoreCard';
import { StrategyPanel } from '../components/StrategyPanel';
import { TradePlanCard } from '../components/TradePlanCard';
import { RiskFlags } from '../components/RiskFlags';
import { SevenStepBreakdown } from '../components/SevenStepBreakdown';
import { JsonExport } from '../components/JsonExport';
import { AlertCircle, Lock, Crown, Star } from 'lucide-react';

type ViewState = 'input' | 'loading' | 'result';

export const ScannerPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { userData, loading: userLoading, incrementScan, error: userError } = useUserData();

  const [view, setView] = useState<ViewState>('input');
  const [ticker, setTicker] = useState('');
  const [progress, setProgress] = useState({ step: '', percent: 0 });
  const [result, setResult] = useState<TickerAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [riskTolerance, setRiskTolerance] = useState<5 | 10>(5);

  // Restore cached analysis result on mount (so Back button preserves state)
  useEffect(() => {
    const tickerParam = searchParams.get('ticker');
    if (!tickerParam) {
      try {
        const cached = sessionStorage.getItem('lastAnalysis');
        if (cached) {
          const parsed = JSON.parse(cached) as TickerAnalysis;
          setResult(parsed);
          setTicker(parsed.ticker);
          setView('result');
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  // Handle ticker from URL params
  useEffect(() => {
    const tickerParam = searchParams.get('ticker');
    if (tickerParam && isSignedIn && userData?.canScan) {
      handleAnalyze(tickerParam);
    }
  }, [searchParams, isSignedIn, userData?.canScan]);

  const handleAnalyze = useCallback(async (tickerSymbol: string) => {
    if (!tickerSymbol.trim()) return;

    // Check if user can scan
    if (!userData?.canScan) {
      setError('Daily scan limit reached. Upgrade your plan for more scans!');
      return;
    }

    setTicker(tickerSymbol.toUpperCase());
    setView('loading');
    setError(null);
    setProgress({ step: 'Initializing V3 Engine...', percent: 0 });

    try {
      const analysis = await runFullAnalysis(
        tickerSymbol.toUpperCase(),
        (step, percent) => setProgress({ step, percent }),
        riskTolerance
      );

      // Track the scan
      const tracked = await incrementScan(
        tickerSymbol.toUpperCase(),
        analysis.scores.normalizedTotal,
        analysis.status
      );

      if (!tracked) {
        console.warn('Failed to track scan');
      }

      setResult(analysis);
      setView('result');

      // Cache result so Back button from Moonshot restores it
      try {
        sessionStorage.setItem('lastAnalysis', JSON.stringify(analysis));
      } catch (e) {
        // sessionStorage full or unavailable - non-critical
      }
    } catch (err) {
      console.error('Analysis failed:', err);
      setError(err instanceof Error ? err.message : 'Analysis failed. Please try again.');
      setView('input');
    }
  }, [riskTolerance, userData, incrementScan]);

  const handleNewAnalysis = useCallback(() => {
    setView('input');
    setResult(null);
    setTicker('');
    sessionStorage.removeItem('lastAnalysis');
  }, []);

  // Loading state
  if (!isLoaded || userLoading) {
    return (
      <div className="min-h-screen bg-terminal-bg flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-terminal-dim">Loading scanner...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <Lock className="w-16 h-16 text-terminal-dim mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Sign In Required</h1>
            <p className="text-terminal-dim">
              Please sign in to access the options scanner.
            </p>
          </div>
          <div className="space-y-3">
            <Link
              to="/login"
              className="block w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold hover:opacity-90"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="block w-full py-3 px-6 bg-terminal-card border border-terminal-border text-white rounded-lg font-medium hover:bg-slate-800"
            >
              Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No scans available
  if (!userData?.canScan && view === 'input') {
    return (
      <div className="min-h-screen bg-terminal-bg">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-16 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="relative">
                  <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                    7S
                  </div>
                </div>
                <span className="text-terminal-bright font-bold tracking-tight text-lg">
                  ALPHA<span className="text-cyan-400">OPTIONS</span>
                </span>
              </Link>
              <Link to="/dashboard" className="text-terminal-dim hover:text-white text-sm">
                Dashboard
              </Link>
            </div>
          </div>
        </header>

        <div className="flex flex-col items-center justify-center p-4 min-h-[calc(100vh-64px)]">
          <div className="max-w-md text-center">
            <div className="mb-6">
              <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">
                {userData?.effectiveTier === 'free' ? 'Subscription Required' : 'Daily Limit Reached'}
              </h1>
              <p className="text-terminal-dim">
                {userData?.effectiveTier === 'free'
                  ? 'You need an active subscription to use the scanner.'
                  : `You've used all ${userData?.maxScans ?? 0} scans for today. Your limit resets at midnight UTC.`
                }
              </p>
            </div>

            <div className="bg-terminal-card rounded-xl border border-terminal-border p-6 mb-6">
              <div className="text-terminal-dim text-sm mb-2">Today's Usage</div>
              <div className="text-3xl font-bold text-white mb-2">
                {userData?.scansToday ?? 0} / {userData?.maxScans ?? 0}
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-rose-500 rounded-full w-full" />
              </div>
            </div>

            <div className="space-y-3">
              <Link
                to="/pricing"
                className="flex items-center justify-center gap-2 w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-lg font-bold hover:opacity-90"
              >
                <Crown className="w-5 h-5" />
                {userData?.effectiveTier === 'free' ? 'View Plans' : 'Upgrade for More Scans'}
              </Link>
              <Link
                to="/dashboard"
                className="block w-full py-3 px-6 bg-terminal-card border border-terminal-border text-white rounded-lg font-medium hover:bg-slate-800"
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-terminal-bg text-terminal-text font-mono selection:bg-cyan-500/30">
      {/* Scanline Effect */}
      <div className="fixed inset-0 pointer-events-none z-50 bg-scanlines opacity-[0.02]" />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs shadow-lg shadow-cyan-500/20">
                  7S
                </div>
                <div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-emerald-400 rounded-full animate-pulse" />
              </div>
              <div className="hidden sm:block">
                <span className="text-terminal-bright font-bold tracking-tight text-lg">
                  ALPHA<span className="text-cyan-400">OPTIONS</span>
                </span>
                <span className="text-terminal-dim text-sm ml-2 border border-terminal-border px-1.5 py-0.5 rounded bg-terminal-card hidden md:inline">v3.1</span>
              </div>
            </Link>

            {/* Status Bar */}
            <div className="flex items-center gap-2 sm:gap-4 text-xs">
              <div className="hidden md:flex items-center gap-2 text-terminal-dim">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>SYSTEM ONLINE</span>
              </div>

              {/* Scans Remaining - Compact on mobile */}
              <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 bg-terminal-card border border-terminal-border rounded-lg">
                <span className="hidden sm:inline text-terminal-dim">Scans:</span>
                <span className={`font-bold ${userData?.remainingScans && userData.remainingScans <= 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {userData?.effectiveTier === 'admin' ? '∞' : userData?.remainingScans}/{userData?.effectiveTier === 'admin' ? '∞' : userData?.maxScans}
                </span>
              </div>

              {/* Tier Badge - Shorter on mobile */}
              <div className={`px-2 py-0.5 rounded text-xs font-bold border whitespace-nowrap ${
                userData?.effectiveTier === 'elite' || userData?.effectiveTier === 'promo' || userData?.effectiveTier === 'admin'
                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                  : userData?.effectiveTier === 'trial'
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                  : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
              }`}>
                <span className="hidden sm:inline">{userData?.effectiveTier?.toUpperCase()}{userData?.hasValidTrial && userData?.trialDaysRemaining ? ` (${userData.trialDaysRemaining}d)` : ''}</span>
                <span className="sm:hidden">{userData?.effectiveTier?.slice(0, 3).toUpperCase()}</span>
              </div>

              <Link to="/dashboard" className="hidden sm:block text-terminal-dim hover:text-white">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Input View - fills viewport */}
        {view === 'input' && (
          <div className="max-w-4xl mx-auto animate-fadeIn flex flex-col justify-center min-h-[calc(100vh-200px)]">
            <div className="text-center mb-10">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-5 tracking-tight">
                Market Edge <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">Decoded.</span>
              </h1>
              <p className="text-terminal-dim text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed">
                Institutional-grade options analysis framework.
                <br />Scan stocks for probability-weighted trade setups.
              </p>
            </div>

            {/* Risk Tolerance Selector */}
            <div className="mb-8 bg-terminal-card rounded-xl border border-terminal-border p-1.5 max-w-xl mx-auto w-full">
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => setRiskTolerance(5)}
                  className={`p-4 rounded-lg transition-all text-center ${
                    riskTolerance === 5
                      ? 'bg-slate-800 text-cyan-400 font-bold shadow-md'
                      : 'text-terminal-dim hover:text-white'
                  }`}
                >
                  <div className="text-base font-medium">Conservative (5%)</div>
                </button>
                <button
                  onClick={() => setRiskTolerance(10)}
                  className={`p-4 rounded-lg transition-all text-center ${
                    riskTolerance === 10
                      ? 'bg-slate-800 text-amber-400 font-bold shadow-md'
                      : 'text-terminal-dim hover:text-white'
                  }`}
                >
                  <div className="text-base font-medium">Aggressive (10%)</div>
                </button>
              </div>
            </div>

            <div className="max-w-xl mx-auto w-full">
              <TickerInput
                onAnalyze={handleAnalyze}
                disabled={!userData?.canScan}
              />
            </div>

            {error && (
              <div className="mt-6 p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400 text-center animate-shake max-w-xl mx-auto">
                <span className="font-bold mr-2">Error:</span> {error}
              </div>
            )}

            {userError && (
              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-center max-w-xl mx-auto">
                {userError}
              </div>
            )}

            {/* Footer Stats */}
            <div className="mt-10 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-8 text-center opacity-60 max-w-2xl mx-auto">
              <div className="py-2 sm:py-0">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-300">7-Step</div>
                <div className="text-xs sm:text-sm md:text-base text-slate-300 uppercase">Logic Engine</div>
              </div>
              <div className="py-2 sm:py-0">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-300">Real-time</div>
                <div className="text-xs sm:text-sm md:text-base text-slate-300 uppercase">Greeks Data</div>
              </div>
              <div className="py-2 sm:py-0">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-300">Defined</div>
                <div className="text-xs sm:text-sm md:text-base text-slate-300 uppercase">Risk Outcomes</div>
              </div>
            </div>
          </div>
        )}

        {/* Loading View */}
        {view === 'loading' && (
          <AnalysisProgress
            ticker={ticker}
            step={progress.step}
            percent={progress.percent}
          />
        )}

        {/* Result View */}
        {view === 'result' && result && (
          <div className="space-y-6 animate-fadeIn pb-20">
            {/* Analyse Another Ticker Bar */}
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-4 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-xs text-terminal-dim uppercase font-bold">Current:</div>
                <div className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 font-bold text-lg">
                  {result.ticker}
                </div>
                <div className="text-sm text-terminal-dim">${result.currentPrice.toFixed(2)}</div>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const input = form.elements.namedItem('newTicker') as HTMLInputElement;
                  const val = input.value.trim().toUpperCase();
                  if (val) handleAnalyze(val);
                }}
                className="flex-1 flex items-center gap-2 w-full sm:w-auto"
              >
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-terminal-dim text-sm">$</span>
                  <input
                    name="newTicker"
                    type="text"
                    placeholder="New ticker..."
                    maxLength={5}
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full bg-slate-900 border border-terminal-border rounded-lg pl-7 pr-4 py-2.5 text-white font-bold placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none text-base"
                  />
                </div>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-lg hover:opacity-90 transition-opacity text-sm whitespace-nowrap"
                >
                  Analyse
                </button>
              </form>
            </div>

            {/* Top Row: Score + Status */}
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-1/3">
                <ScoreCard analysis={result} />
              </div>
              <div className="lg:w-2/3">
                <RiskFlags flags={result.riskFlags} />
              </div>
            </div>

            {/* Strategy Panel (Includes Payoff Chart) */}
            <StrategyPanel
              strategies={result.strategies}
              bestStrategy={result.bestStrategy}
              status={result.status}
              currentPrice={result.currentPrice}
              ticker={result.ticker}
              analysis={result}
            />

            {/* Position Sizing Summary */}
            {result.positionSizing && (
              <div className="p-4 bg-terminal-card rounded-xl border border-terminal-border shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-terminal-bright uppercase tracking-wider">
                    Position Sizing Calculator
                  </h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                    result.positionSizing.canAfford
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-rose-500/20 text-rose-400'
                  }`}>
                    {result.positionSizing.canAfford ? 'CAPITAL APPROVED' : 'INSUFFICIENT FUNDS'}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-terminal-dim text-sm uppercase mb-1">Max Risk</div>
                    <div className="text-white font-bold">${result.positionSizing.maxRiskDollars.toFixed(0)}</div>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-terminal-dim text-sm uppercase mb-1">Rec. Contracts</div>
                    <div className="text-cyan-400 font-bold text-xl">{result.positionSizing.recommendedContracts}</div>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-terminal-dim text-sm uppercase mb-1">Capital Req.</div>
                    <div className="text-white font-bold">${result.positionSizing.capitalRequired.toFixed(0)}</div>
                  </div>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <div className="text-terminal-dim text-sm uppercase mb-1">VIX Adjustment</div>
                    <div className={`font-bold ${result.positionSizing.vixAdjustment < 1 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {(result.positionSizing.vixAdjustment * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trade Plan (if approved) */}
            {result.tradePlan && result.bestStrategy && (
              <TradePlanCard
                plan={result.tradePlan}
                strategy={result.bestStrategy}
                ticker={result.ticker}
                price={result.currentPrice}
                analysis={result}
              />
            )}

            {/* 7-Step Breakdown */}
            <SevenStepBreakdown scores={result.scores} />

            {/* Export & Reset */}
            <div className="flex flex-col items-center gap-4 mt-8">
              <JsonExport analysis={result} />

              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-8 py-3 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 hover:text-white hover:border-cyan-500/50 transition-all flex items-center gap-2 font-bold"
              >
                <span>Analyse Another Ticker</span>
                <span>↑</span>
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ScannerPage;
